import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { useTranslation } from 'next-i18next';
import React from 'react';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import AppleSignIn from '@/components/layout/AppleSignIn';
import { NS } from '@/constants';
import { FEATURES } from '@/constants/features';
import { URLS } from '@/constants/urls';
import { useFeature } from '@/hooks';
import { useAppDispatch, useAppSelector } from '@/redux';
import { connectSocialNetworkUser } from '@/redux/slices/auth.slice';
import { LoggerService } from '@/services';
import { CallToActionKind } from '@/types';

type SocialLoginButtonsProps = {
	fullWidth?: boolean;
};

declare const AppleID: any;

const PROVIDER = {
	APPLE: 'apple',
	GOOGLE: 'google',
};

export const SocialLoginButtons = ({ fullWidth = false }: SocialLoginButtonsProps): React.JSX.Element => {
	const clientId = process.env.NEXT_PUBLIC_GOOGLE_APP_ID;

	return (
		<GoogleOAuthProvider clientId={clientId}>
			<InnerComponent fullWidth={fullWidth} />
		</GoogleOAuthProvider>
	);
};

const InnerComponent = ({ fullWidth = false }: SocialLoginButtonsProps): React.JSX.Element => {
	const { t } = useTranslation(NS.COMMON);
	const dispatch = useAppDispatch();
	const storeConf = useAppSelector(({ root }) => root.storeConf);
	const appleSignInFeatureEnabled = useFeature(storeConf, FEATURES.APPLE_SIGN_IN);
	const googleLoginFeatureEnabled = useFeature(storeConf, FEATURES.GOOGLE_LOGIN);
	const redirectURI = storeConf?.url;

	const googleLogin = useGoogleLogin({
		onSuccess: (tokenResponse) => {
			const socialNetworkInformation = {
				credential: tokenResponse?.access_token,
				socialNetworkProvider: PROVIDER.GOOGLE,
			};
			dispatch(connectSocialNetworkUser({ socialNetworkInformation: socialNetworkInformation }));
		},
	});

	const appleLogin = () => {
		const appleSignInState = Math.floor(Math.random() * 100000).toString();
		AppleID.auth.init({
			clientId: 'com.millesima.services',
			redirectURI: redirectURI,
			state: appleSignInState,
			scope: 'name email',
			usePopup: true,
		});

		AppleID.auth
			.signIn()
			.then((res) => {
				const { state, code } = res.authorization;
				const firstName = res.user?.name.firstName;
				const lastName = res.user?.name.lastName;

				if (state === appleSignInState) {
					const socialNetworkInformation = {
						code,
						appleRedirectURI: redirectURI,
						firstName,
						lastName,
						socialNetworkProvider: PROVIDER.APPLE,
					};
					dispatch(connectSocialNetworkUser({ socialNetworkInformation: socialNetworkInformation }));
				}
			})
			.catch((err) => {
				LoggerService.sendLog(null, 'Apple sign in : ' + err.error, storeConf?.storeId, null, null, null);
			});
	};

	return (
		<>
			<AppleSignIn />
			{googleLoginFeatureEnabled && (
				<CallToActionContainer
					id="social-google"
					kind={CallToActionKind.SECONDARY}
					fullWidth={fullWidth}
					onClick={googleLogin}
					icon={{ url: URLS.SOCIAL_GOOGLE, width: 16, height: 16 }}
				>
					{t('forms.signInGoogle')}
				</CallToActionContainer>
			)}
			{appleSignInFeatureEnabled && (
				<CallToActionContainer
					id="social-apple"
					kind={CallToActionKind.SECONDARY}
					fullWidth={fullWidth}
					onClick={appleLogin}
					icon={{ url: URLS.SOCIAL_APPLE, width: 16, height: 16 }}
				>
					{t('forms.signInApple')}
				</CallToActionContainer>
			)}
		</>
	);
};

export default SocialLoginButtons;
